import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useOrg } from '../../contexts/OrgContext'
import { useAIJobs } from '../../contexts/AIJobsContext'
import { useAssessment } from '../../contexts/AssessmentContext'
import { useInsightsData } from '../../features/insights/hooks/useInsightsData'
import { useDemoPlan } from '../../hooks/useDemoPlan'
import { buildAdvisoryReportContext } from '../utils/advisoryReportAggregator'
import { exportAdvisoryReportDocx } from '../utils/advisoryReportDocxExport'
import { exportDiscoveryDataXlsx } from '../utils/discoveryDataExport'
import { getTopPriorities } from '../utils/scoring'

// Action bar that appears when a user selects an assessment row.
// Exposes two actions:
//   - Generate Executive Report → .docx download (POC: Exec Summary section)
//   - Download Assessment Data → .xlsx multi-sheet discovery dump
function SelectedAssessmentActions({ assessmentId, client, onClose }) {
  const { currentOrg } = useOrg()
  const { startJob, getJob } = useAIJobs()
  const { getAssessment } = useAssessment()
  const { raw } = useInsightsData(assessmentId)
  const { isDemoOrg, demoMessage } = useDemoPlan()

  const [orgBrandConfig, setOrgBrandConfig] = useState(null)
  const [clientEnrichment, setClientEnrichment] = useState(null)
  const [docxError, setDocxError] = useState(null)
  const [xlsxError, setXlsxError] = useState(null)
  const [xlsxRunning, setXlsxRunning] = useState(false)
  const [demoNotice, setDemoNotice] = useState('')

  const jobKey = assessmentId ? `advisory-report:${assessmentId}` : null
  const job = jobKey ? getJob(jobKey) : null
  const reportRunning = job?.status === 'running'

  useEffect(() => {
    if (!currentOrg?.id) return
    supabase.from('org_brand_config').select('*').eq('org_id', currentOrg.id).maybeSingle()
      .then(({ data }) => { if (data) setOrgBrandConfig(data) })
  }, [currentOrg?.id])

  useEffect(() => {
    if (!client?.id) return
    supabase.from('client_enrichment').select('*').eq('client_id', client.id).maybeSingle()
      .then(({ data }) => { if (data) setClientEnrichment(data) })
  }, [client?.id])

  const { assessment } = getAssessment(assessmentId) || {}
  const canGenerateReport = Boolean(assessmentId && raw?.scores && client)
  const canDownloadData = Boolean(assessmentId && assessment && client)

  const reportDisabledReason = !assessmentId
    ? 'Select an assessment'
    : !assessment
    ? 'Assessment data not loaded yet'
    : !raw?.scores
    ? 'Not enough maturity responses to compute scores'
    : null

  const dataDisabledReason = !assessmentId
    ? 'Select an assessment'
    : !assessment
    ? 'Assessment data not loaded yet'
    : null

  const handleGenerateReport = async () => {
    if (isDemoOrg) {
      setDemoNotice(demoMessage('Advisory Report exports'))
      setTimeout(() => setDemoNotice(''), 8000)
      return
    }
    if (!canGenerateReport) return
    setDocxError(null)

    const priorities = raw?.scores ? getTopPriorities(raw.scores, raw.alignments, raw.benchmarkData) : []
    const applicableFrameworks =
      raw?.benchmarkData?.industryInfo?.keyFrameworks ||
      clientEnrichment?.enrichment?.regulatoryProfile?.applicableFrameworks ||
      []

    const context = buildAdvisoryReportContext({
      client,
      assessment,
      scores: raw.scores,
      benchmarkData: raw.benchmarkData,
      alignments: raw.alignments,
      priorities,
      clientEnrichment,
      applicableFrameworks
    })

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const result = await startJob(jobKey, async () => {
        const res = await fetch('/api/ai/advisory-report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
          },
          body: JSON.stringify({ section: 'executive-summary', context })
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.error || 'Advisory report generation failed')
        }
        return await res.json()
      })

      await exportAdvisoryReportDocx({
        client,
        context,
        draft: result?.draft,
        review: result?.review,
        brand: {
          firmName: orgBrandConfig?.firm_display_name || currentOrg?.name || 'Sidewalk Security Advisors',
          logoUrl: orgBrandConfig?.logo_url || null,
          primaryHex: orgBrandConfig?.primary_color || '0D1B2A',
          accentHex: orgBrandConfig?.accent_color || 'C9A84C',
          trademarkLine: orgBrandConfig?.trademark_line || null,
          font: orgBrandConfig?.font_preference || 'Calibri'
        }
      })
    } catch (err) {
      console.error('[AdvisoryReport] DOCX generation failed:', err)
      setDocxError(err?.message || 'Advisory report failed')
    }
  }

  const handleDownloadData = async () => {
    if (!canDownloadData) return
    setXlsxError(null)
    setXlsxRunning(true)
    try {
      await exportDiscoveryDataXlsx({ client, assessment, scores: raw?.scores || null })
    } catch (err) {
      console.error('[DiscoveryData] Export failed:', err)
      setXlsxError(err?.message || 'Data export failed')
    } finally {
      setXlsxRunning(false)
    }
  }

  if (!assessmentId) return null

  return (
    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-grow min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-300">
            Selected Assessment
          </p>
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate mt-0.5">
            {assessment?.title || `Assessment - ${assessment?.createdAt ? new Date(assessment.createdAt).toLocaleDateString() : ''}`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {assessment?.status === 'completed'
              ? `Completed ${new Date(assessment.completedAt).toLocaleDateString()}`
              : `In progress`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={reportRunning || !canGenerateReport}
            title={reportDisabledReason || 'Generate Executive Advisory Report as .docx'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
          >
            {reportRunning ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Executive Report (.docx)
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleDownloadData}
            disabled={xlsxRunning || !canDownloadData}
            title={dataDisabledReason || 'Download all Discover data as .xlsx spreadsheet'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
          >
            {xlsxRunning ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Assessment Data (.xlsx)
              </>
            )}
          </button>
          <Link
            to={`/assessment/${assessmentId}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Clear selection"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      {(docxError || xlsxError || demoNotice) && (
        <div className="mt-2 space-y-1">
          {docxError && <p className="text-xs text-red-600 dark:text-red-400">Report error: {docxError}</p>}
          {xlsxError && <p className="text-xs text-red-600 dark:text-red-400">Data export error: {xlsxError}</p>}
          {demoNotice && <p className="text-xs text-amber-700 dark:text-amber-400">{demoNotice}</p>}
        </div>
      )}
    </div>
  )
}

export default SelectedAssessmentActions
